import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Building2,
  Calculator,
  ChevronUp,
  ChevronLeft,
  ShieldCheck,
  Package,
  LogOut,
  Monitor,
  Moon,
  Sun,
  User,
  Users,
  Settings,
  ClipboardList,
  Sparkles,
  Search,
  LineChart,
  BarChart3,
  Layers,
  Database,
  MessageSquareReply,
  Truck,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { isFeatureEnabledForUser, type FeatureKey } from '@carrierbridge/shared';
import { cn } from '@/lib/utils';
import { carriersListQueryOptions } from '@/hooks/useCarriers';
import { useLogout } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import { useAuthContext } from '@/contexts/AuthContext';
import { useBranding } from '@/hooks/useBranding';
import { canAnswerQuoteRequests } from '@/pages/quote-response/quote-response-access';
import { LanguageSelect } from './LanguageSelect';

interface NavItemType {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  matchPrefix?: string;
  /** Paramètre d'URL requis pour distinguer plusieurs entrées d'une même page. */
  searchParam?: { key: string; value: string; defaultValue?: string };
  /** Exact-match highlighting (so a parent route doesn't light up on its sub-routes). */
  end?: boolean;
  /** Traitement signature (halo dégradé au survol) — réservé à Ask Robert. */
  signature?: boolean;
  /** Survol de l'entrée — sert au prefetch des données de la page cible. */
  onHover?: () => void;
}

const COLLAPSED_KEY = 'cb-sidebar-collapsed';

function NavGroup({ title, collapsed, children }: { title?: string; collapsed: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      {title && !collapsed && (
        // relative z-[1] : reste au-dessus de la pastille coulissante (elle-même
        // en absolu) — sans ça, son dégradé passe par-dessus et « efface » le
        // libellé de section quand elle glisse à proximité.
        <p className="relative z-[1] px-3 mb-1 h-[18px] flex items-center text-[10px] font-semibold uppercase tracking-[0.16em] text-subtle">
          {title}
        </p>
      )}
      {title && collapsed && <div className="h-2" aria-hidden />}
      {children}
    </div>
  );
}

/**
 * Pastille active coulissante : un seul élément accentué qui glisse d'un item
 * à l'autre (barrette indigo + fond dégradé), au lieu d'un fond qui apparaît
 * et disparaît. Positionnée en absolu dans sa zone, pilotée par offsetTop.
 *
 * Elle doit rester hors de tout conteneur `space-y-*` : ces utilitaires
 * marginent « tous les enfants sauf le premier » sans distinguer les éléments
 * hors flux. Montée seulement une fois `top` connu, la pastille prendrait
 * sinon la place de premier enfant et pousserait les items d'un cran APRÈS
 * la mesure — d'où un décalage au premier clic. Les items vivent donc dans un
 * wrapper interne qui porte l'espacement, la pastille en frère de ce wrapper.
 */
function SlidingPill({ top }: { top: number | null }) {
  if (top == null) return null;
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute left-2.5 right-2.5 h-9 rounded-lg bg-gradient-to-r from-accent-soft via-accent-soft/40 to-transparent transition-[top] duration-300 ease-out motion-reduce:transition-none"
      style={{ top }}
    >
      <div className="absolute -left-2.5 top-2 bottom-2 w-[3px] rounded-r-full bg-accent" />
    </div>
  );
}

function UserMenu({
  user, logout, t, collapsed,
}: {
  user: { name: string; email: string; role: string; company?: { name: string } | null };
  /** Asynchrone : révoque le refresh token côté serveur avant de purger. */
  logout: () => void | Promise<void>;
  t: (key: string) => string;
  collapsed: boolean;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const isSuperAdmin = user.role === 'superadmin';
  // Organisation dans laquelle la session est ouverte : une personne peut avoir un
  // compte dans plusieurs sociétés, et rien d'autre à l'écran ne dit laquelle.
  // Absente pour un superadmin (aucune société).
  const companyName = user.company?.name;
  const identityLabel = [user.name, companyName].filter(Boolean).join(' · ');
  // La page Équipe est réservée aux administrateurs (App.tsx) : l'entrée ne
  // doit pas mener un éditeur sur un écran « Accès non disponible ».
  const canManageTeam = user.role === 'admin';

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  const menuItems = isSuperAdmin
    ? [
        { label: t('nav.superAdmin'), icon: ShieldCheck, to: '/superadmin', accent: true },
        { label: t('nav.myAccount'), icon: User, to: '/settings/account' },
      ]
    : [
        { label: t('nav.myAccount'), icon: User, to: '/settings/account' },
        ...(canManageTeam ? [{ label: t('nav.team'), icon: Users, to: '/settings/team' }] : []),
        { label: t('nav.settings'), icon: Settings, to: '/settings' },
      ];

  const isActive =
    location.pathname.startsWith('/settings') ||
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/superadmin');

  return (
    <div ref={menuRef} className={cn('relative', collapsed ? 'px-2 py-3' : 'px-4 py-3')}>
      {open && (
        <div className={cn(
          'absolute bottom-full mb-2 rounded border border-border bg-surface shadow-md py-1 z-50',
          collapsed ? 'left-2 w-52' : 'left-3 right-3',
        )}>
          {menuItems.map((item) => {
            const active = item.to === '/superadmin'
              ? location.pathname === '/superadmin'
              : item.to === '/settings'
                ? location.pathname === '/settings' || (location.pathname.startsWith('/settings/') && !['/settings/account', '/settings/team'].includes(location.pathname))
                : location.pathname === item.to;
            return (
              <button
                key={item.label}
                onClick={() => {
                  navigate(item.to);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center gap-3 px-3 py-2 text-[13px] font-medium transition-colors',
                  active
                    ? item.accent ? 'bg-warning-soft text-warning' : 'bg-accent-soft text-accent'
                    : item.accent
                      ? 'text-muted hover:bg-warning-soft hover:text-warning'
                      : 'text-muted hover:bg-surface-subtle hover:text-text',
                )}
              >
                <item.icon className={cn(
                  'h-4 w-4 flex-shrink-0',
                  active
                    ? item.accent ? 'text-warning' : 'text-accent'
                    : 'text-subtle',
                )} />
                {item.label}
              </button>
            );
          })}
          <div className="border-t border-border mt-1 pt-1">
            <button
              onClick={() => { logout(); setOpen(false); }}
              className="flex w-full items-center gap-3 px-3 py-2 text-[13px] font-medium text-muted hover:bg-surface-subtle hover:text-text transition-colors"
            >
              <LogOut className="h-4 w-4 flex-shrink-0 text-subtle" />
              {t('nav.logout')}
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen(!open)}
        className={cn(
          'flex w-full items-center rounded py-1.5 transition-colors',
          collapsed ? 'justify-center px-0' : 'gap-3 px-2',
          isActive ? 'bg-accent-soft' : 'hover:bg-surface-subtle',
        )}
        title={collapsed ? identityLabel : undefined} aria-label={collapsed ? identityLabel : undefined}
      >
        <div className={cn(
          'flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold flex-shrink-0',
          isActive
            ? 'bg-accent text-white'
            : 'bg-accent-soft text-accent',
        )}>
          {user.name.charAt(0).toUpperCase()}
        </div>
        {!collapsed && (
          <>
            <div className="min-w-0 flex-1 text-left">
              <p className="text-xs font-medium text-text truncate">{user.name}</p>
              {companyName && (
                <p className="flex items-center gap-1 text-2xs font-medium text-muted">
                  <Building2 aria-hidden className="h-3 w-3 flex-shrink-0" />
                  <span className="sr-only">{t('nav.currentOrganization')} : </span>
                  <span className="truncate">{companyName}</span>
                </p>
              )}
              <p className="text-2xs text-subtle truncate">{user.email}</p>
            </div>
            <ChevronUp className={cn(
              'h-3.5 w-3.5 flex-shrink-0 transition-transform duration-150',
              open ? 'text-accent' : 'text-subtle rotate-180',
            )} />
          </>
        )}
      </button>
    </div>
  );
}

function BrandLogo({ branding }: { branding: { logoUrl?: string | null; accentColor?: string } | null | undefined }) {
  const [imgError, setImgError] = useState(false);
  const lastUrl = useRef(branding?.logoUrl);

  if (branding?.logoUrl !== lastUrl.current) {
    lastUrl.current = branding?.logoUrl;
    setImgError(false);
  }

  if (branding?.logoUrl && !imgError) {
    return (
      <img
        src={branding.logoUrl}
        alt=""
        className="h-8 w-8 flex-shrink-0 object-contain rounded"
        onError={() => setImgError(true)}
      />
    );
  }

  return (
    <div
      className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded"
      style={{ backgroundColor: branding?.accentColor || '#4F46E5' }}
    >
      <Package className="h-4 w-4 text-white" />
    </div>
  );
}

export function Sidebar() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { user } = useAuthContext();
  const logout = useLogout();
  const { preference: themePreference, toggle: toggleTheme } = useTheme();
  const queryClient = useQueryClient();

  const { data: branding } = useBranding();

  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSED_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0'); } catch { /* stockage indisponible */ }
  }, [collapsed]);

  const isSuperAdmin = user.role === 'superadmin';
  const canManage = ['superadmin', 'admin', 'editor'].includes(user.role) && !isSuperAdmin;

  // Options par société. Les défauts viennent de FEATURE_DEFAULTS (source de
  // vérité partagée) : les modules optionnels neufs (dont operations) sont
  // OFF par défaut — cachés, vendus à la carte. Les superadmins gardent la
  // nav complète pour pouvoir déboguer n'importe quel tenant.
  const has = (key: FeatureKey) => isFeatureEnabledForUser(user, key);
  const hasOperations = has('operations');
  const hasTenders = has('tenders');
  const hasRobert = has('robert');
  // Module `quoteResponse` ET lecture des prix d'achat : la même règle que la page et que l'API.
  const hasQuoteResponse = canAnswerQuoteRequests(user);

  function isItemActive(item: NavItemType): boolean {
    if (item.searchParam) {
      if (location.pathname !== item.to.split('?')[0]) return false;
      const params = new URLSearchParams(location.search);
      return (params.get(item.searchParam.key) ?? item.searchParam.defaultValue) === item.searchParam.value;
    }
    if (item.matchPrefix && location.pathname.startsWith(item.matchPrefix)) return true;
    const end = item.end || item.to === '/carriers' || item.to === '/logistics-sites';
    return end ? location.pathname === item.to : location.pathname.startsWith(item.to);
  }

  // Pastille coulissante : une par zone (nav principale / zone épinglée),
  // repositionnée sur l'item actif à chaque navigation.
  const navRef = useRef<HTMLElement>(null);
  const footRef = useRef<HTMLDivElement>(null);
  const [navPillTop, setNavPillTop] = useState<number | null>(null);
  const [footPillTop, setFootPillTop] = useState<number | null>(null);

  useLayoutEffect(() => {
    const measure = (root: HTMLElement | null): number | null => {
      const el = root?.querySelector<HTMLElement>('[data-active="true"]');
      return el ? el.offsetTop : null;
    };
    setNavPillTop(measure(navRef.current));
    setFootPillTop(measure(footRef.current));
  }, [location.pathname, location.search, collapsed, i18n.language, user.role, hasOperations, hasTenders, hasRobert, hasQuoteResponse]);

  function NavItem({ item }: { item: NavItemType }) {
    const active = isItemActive(item);
    return (
      <NavLink
        to={item.to}
        end={item.end || item.to === '/carriers' || item.to === '/logistics-sites'}
        data-active={active || undefined}
        title={collapsed ? item.label : undefined}
        onMouseEnter={item.onHover}
        className={cn(
          'group relative z-[1] flex h-9 items-center rounded-lg text-[13px] font-medium transition-colors duration-150',
          collapsed ? 'justify-center px-0' : 'gap-3 px-3',
          active
            ? 'text-accent font-semibold'
            : 'text-muted hover:bg-surface-subtle/60 hover:text-text',
        )}
      >
        {item.signature && (
          <span
            aria-hidden
            className={cn(
              'absolute inset-0 rounded-lg bg-gradient-to-r from-accent/10 via-fuchsia-500/5 to-transparent transition-opacity duration-200',
              active ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
            )}
          />
        )}
        <item.icon
          className={cn(
            'relative h-[18px] w-[18px] flex-shrink-0 transition-transform duration-150 group-hover:translate-x-px',
            active ? 'text-accent' : 'text-subtle group-hover:text-muted',
            item.signature && !active && 'text-accent/70',
          )}
        />
        {!collapsed && <span className="relative truncate">{item.label}</span>}
      </NavLink>
    );
  }

  return (
    <aside
      className={cn(
        'flex h-full flex-col bg-surface border-r border-border transition-[width] duration-300 ease-out motion-reduce:transition-none',
        collapsed ? 'w-16' : 'w-[260px]',
      )}
    >
      {/* Brand header */}
      <div
        className={cn(
          'flex flex-shrink-0 border-b border-border',
          collapsed
            ? 'flex-col items-center gap-1.5 px-2 py-2.5'
            : 'h-14 items-center gap-2.5 px-4',
        )}
      >
        <BrandLogo branding={branding} />
        {!collapsed && (
          <div className="flex flex-col min-w-0 flex-1">
            <span className="text-[13px] font-semibold tracking-tight text-text truncate">
              {branding?.brandName || t('brand.pricing')}
            </span>
            <span className="text-2xs text-subtle -mt-0.5">{t('brand.transport')}</span>
          </div>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          title={collapsed ? t('nav.expandMenu') : t('nav.collapseMenu')}
          aria-label={collapsed ? t('nav.expandMenu') : t('nav.collapseMenu')}
          className="hidden lg:grid h-6 w-6 flex-shrink-0 place-items-center rounded-md text-subtle transition-colors hover:bg-surface-subtle hover:text-text"
        >
          <ChevronLeft className={cn('h-3.5 w-3.5 transition-transform duration-300', collapsed && 'rotate-180')} />
        </button>
      </div>

      {/* Navigation */}
      <nav
        ref={navRef}
        aria-label={t('nav.mainNavigation')}
        className="relative flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin px-2.5 py-3"
      >
        <SlidingPill top={navPillTop} />
        <div className="space-y-4">
          {isSuperAdmin ? (
            <div className="space-y-0.5">
              <NavItem item={{ to: '/superadmin', label: t('nav.superAdmin'), icon: ShieldCheck }} />
              <NavItem item={{ to: '/settings/account', label: t('nav.myAccount'), icon: User }} />
            </div>
          ) : (
            <>
              <div className="space-y-0.5">
                {hasRobert && (
                  <NavItem
                    item={{
                      to: '/robert',
                      label: t('nav.askRobert'),
                      icon: Sparkles,
                      signature: true,
                    }}
                  />
                )}
              </div>

              {/* Réseau — annuaire, recherche et appels d'offres. */}
              {canManage && (
                <NavGroup title={t('nav.groups.network')} collapsed={collapsed}>
                  <NavItem
                    item={{
                      to: '/carriers',
                      label: t('nav.groups.carriers'),
                      icon: Truck,
                      matchPrefix: '/carriers',
                      // Prefetch de la liste au survol (même clé/queryFn que la
                      // page /carriers, lifecycle ALL) — no-op si cache frais.
                      onHover: () => queryClient.prefetchQuery({ ...carriersListQueryOptions('ALL'), staleTime: 30_000 }),
                    }}
                  />
                  {has('sourcing') && (
                    <NavItem item={{ to: '/sourcing', label: t('nav.sourcing'), icon: Search, matchPrefix: '/sourcing' }} />
                  )}
                  {hasTenders && (
                    <NavItem item={{ to: '/tenders', label: t('nav.tenders'), icon: ClipboardList, matchPrefix: '/tenders' }} />
                  )}
                </NavGroup>
              )}

              {/* Opérations — tarification puis traitement des demandes. */}
              {canManage && (
                <NavGroup title={t('nav.groups.operations')} collapsed={collapsed}>
                  {has('quote') && (
                    // end : sans lui, le test de préfixe d'isItemActive éclairerait aussi `/quote-response`.
                    <NavItem item={{ to: '/quote', label: t('nav.quote'), icon: Calculator, end: true }} />
                  )}
                  {/* Option à part, sous le Devis : le module peut être pris sans lui. */}
                  {hasQuoteResponse && (
                    <NavItem item={{ to: '/quote-response', label: t('nav.quoteResponse'), icon: MessageSquareReply }} />
                  )}
                  {hasOperations && (
                    <NavItem item={{ to: '/operations', label: t('nav.operations'), icon: Layers, end: true }} />
                  )}
                </NavGroup>
              )}

              {/* Pilotage — les trois vues de l'ancienne page Budget & Analyse. */}
              {canManage && has('budget') && (
                <NavGroup title={t('nav.groups.steering')} collapsed={collapsed}>
                  <NavItem item={{ to: '/analysis?tab=actuals', label: t('nav.actuals'), icon: BarChart3, searchParam: { key: 'tab', value: 'actuals', defaultValue: 'actuals' } }} />
                  {has('budgetAudit') && (
                    <NavItem item={{ to: '/analysis?tab=simulation', label: t('nav.audit'), icon: LineChart, searchParam: { key: 'tab', value: 'simulation' } }} />
                  )}
                  {has('budgetOptimal') && (
                    <NavItem item={{ to: '/analysis?tab=optimal', label: t('nav.optimal'), icon: Truck, searchParam: { key: 'tab', value: 'optimal' } }} />
                  )}
                </NavGroup>
              )}
            </>
          )}
        </div>
      </nav>

      {/* Zone secondaire épinglée : la console Data
          (référentiels, imports, connexions). Les lecteurs sans accès à la
          console gardent un lien direct vers les indices carburant. */}
      {!isSuperAdmin && (
        <div ref={footRef} className="relative flex-shrink-0 border-t border-border px-2.5 py-2.5">
          <SlidingPill top={footPillTop} />
          <div className="space-y-0.5">
            {/* /data éclaire aussi /datasets (même préfixe), ce qui est voulu :
                les pages datasets font partie du domaine Data. */}
            {canManage ? (
              <NavItem
                item={{
                  to: '/data',
                  label: t('nav.data'),
                  icon: Database,
                  matchPrefix: '/data',
                }}
              />
            ) : (
              <NavItem
                item={{
                  to: '/info/fuel-indices',
                  label: t('nav.fuelIndices'),
                  icon: LineChart,
                  matchPrefix: '/info',
                }}
              />
            )}
          </div>
        </div>
      )}

      {/* Langue + thème : deux contrôles discrets sur une ligne, à la place
          de l'ancien sélecteur pleine largeur. En mode rail, seule la langue reste
          (la bascule de thème n'a pas sa place dans 64 px) : sans elle, replier
          le menu rendait la langue introuvable. */}
      {collapsed && (
        <div className="flex flex-shrink-0 items-center justify-center border-t border-border px-2 py-1.5">
          <LanguageSelect className="text-center" />
        </div>
      )}
      {!collapsed && (
        <div className="flex flex-shrink-0 items-center justify-between border-t border-border px-4 py-1.5">
          <LanguageSelect />
          <button
            onClick={toggleTheme}
            title={t(`theme.${themePreference}`)}
            aria-label={t(`theme.${themePreference}`)}
            className="grid h-6 w-6 place-items-center rounded-md text-subtle transition-colors hover:bg-surface-subtle hover:text-text"
          >
            {themePreference === 'light' ? (
              <Sun className="h-3.5 w-3.5" />
            ) : themePreference === 'dark' ? (
              <Moon className="h-3.5 w-3.5" />
            ) : (
              <Monitor className="h-3.5 w-3.5" />
            )}
          </button>
        </div>
      )}

      <div className="flex-shrink-0 border-t border-border">
        <UserMenu user={user} logout={logout} t={t} collapsed={collapsed} />
      </div>
    </aside>
  );
}
